import Cropper from 'cropperjs';
import { getCookie } from './utils.js';
import { addAlert } from './alerts.js';
import { prompt } from './prompts.js';

const ROTATE_STEP = 90;
const FREE_ROTATE_LIMIT = 45;

const ASPECT_RATIOS = [
    { label: gettext('Dowolny'), value: 'free' },
    { label: '1:1', value: '1' },
    { label: '4:3', value: '1.3333333333333333' },
    { label: '16:9', value: '1.7777777777777777' },
];

/**
 * Appends a timestamp to a URL. Edits are applied in place, so the file and its
 * thumbnails keep their URLs and the browser would otherwise serve the stale image.
 */
export function bustCache(url) {
    if (!url) return url;
    return `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`;
}

const TRANSFORM_ACTIONS = [
    {
        action: 'rotate-left',
        icon: 'fa-rotate-left',
        title: gettext('Obróć w lewo'),
    },
    {
        action: 'rotate-right',
        icon: 'fa-rotate-right',
        title: gettext('Obróć w prawo'),
    },
    {
        action: 'flip-x',
        icon: 'fa-arrows-left-right',
        title: gettext('Odbij w poziomie'),
    },
    {
        action: 'flip-y',
        icon: 'fa-arrows-up-down',
        title: gettext('Odbij w pionie'),
    },
];

function buildEditorHtml(imageUrl, ratios) {
    const transformButtons = TRANSFORM_ACTIONS.map(
        ({ action, icon, title }) =>
            `<button type="button" data-action="${action}" title="${title}">
                 <i class="fas ${icon}"></i>
             </button>`,
    ).join('');

    const ratioButtons = ratios
        .map(
            ({ label, value }, index) =>
                `<button type="button" class="${index === 0 ? 'active' : ''}"
                     data-ratio="${value}">${label}</button>`,
        )
        .join('');

    return `
        <div class="image-editor">
            <div class="image-editor-canvas">
                <img class="image-editor-image" src="${imageUrl}" alt="${gettext('Edytowane zdjęcie')}" />
                <div class="image-editor-overlay">
                    <span class="loader"></span>
                    <p>${gettext('Zapisywanie i przetwarzanie miniatur…')}</p>
                </div>
            </div>
            <div class="image-editor-toolbar">
                <div class="image-editor-segment">${transformButtons}</div>
                <div class="image-editor-field">
                    <label for="imageEditorAngle">${gettext('Kąt')}</label>
                    <input type="range" id="imageEditorAngle" min="-${FREE_ROTATE_LIMIT}"
                           max="${FREE_ROTATE_LIMIT}" step="1" value="0" />
                    <output id="imageEditorAngleValue">0°</output>
                </div>
                <div class="image-editor-field">
                    <span class="image-editor-label">${gettext('Kadr')}</span>
                    <div class="image-editor-segment">${ratioButtons}</div>
                </div>
                <button type="button" class="image-editor-reset" data-action="reset">
                    ${gettext('Resetuj')}
                </button>
            </div>
        </div>
    `;
}

/**
 * Rescales the canvas so that the whole rotated image fits the container and
 * resets the crop box to cover it.
 */
function fitRotatedImage(cropper) {
    cropper.clear();

    const container = cropper.getContainerData();
    const canvas = cropper.getCanvasData();
    const scale = Math.min(
        container.width / canvas.naturalWidth,
        container.height / canvas.naturalHeight,
    );
    const width = canvas.naturalWidth * scale;
    const height = canvas.naturalHeight * scale;

    cropper.setCanvasData({
        left: (container.width - width) / 2,
        top: (container.height - height) / 2,
        width,
        height,
    });
    cropper.crop();
}

/**
 * The angle slider is relative to the 90-degree steps, so that using the step
 * buttons does not throw away a fine adjustment and vice versa.
 */
function createCropperController(popup) {
    const image = popup.querySelector('.image-editor-image');
    const angleInput = popup.querySelector('#imageEditorAngle');
    const angleOutput = popup.querySelector('#imageEditorAngleValue');

    let steps = 0;
    let fineAngle = 0;

    const cropper = new Cropper(image, {
        viewMode: 1,
        autoCropArea: 1,
        dragMode: 'move',
        background: false,
        responsive: true,
        checkOrientation: false,
    });

    const applyRotation = () => {
        cropper.rotateTo(steps * ROTATE_STEP + fineAngle);
        fitRotatedImage(cropper);
    };

    const setActiveRatio = (button) => {
        popup
            .querySelectorAll('[data-ratio]')
            .forEach((el) => el.classList.remove('active'));
        button.classList.add('active');
    };

    popup.querySelectorAll('[data-action]').forEach((button) => {
        button.addEventListener('click', () => {
            const { action } = button.dataset;
            if (action === 'rotate-left') {
                steps -= 1;
                applyRotation();
            } else if (action === 'rotate-right') {
                steps += 1;
                applyRotation();
            } else if (action === 'flip-x') {
                cropper.scaleX(cropper.getData().scaleX === -1 ? 1 : -1);
            } else if (action === 'flip-y') {
                cropper.scaleY(cropper.getData().scaleY === -1 ? 1 : -1);
            } else if (action === 'reset') {
                steps = 0;
                fineAngle = 0;
                angleInput.value = 0;
                angleOutput.textContent = '0°';
                cropper.reset();
                cropper.setAspectRatio(NaN);
                setActiveRatio(popup.querySelector('[data-ratio="free"]'));
            }
        });
    });

    angleInput.addEventListener('input', () => {
        fineAngle = Number(angleInput.value);
        angleOutput.textContent = `${fineAngle}°`;
        applyRotation();
    });

    popup.querySelectorAll('[data-ratio]').forEach((button) => {
        button.addEventListener('click', () => {
            const { ratio } = button.dataset;
            cropper.setAspectRatio(ratio === 'free' ? NaN : Number(ratio));
            setActiveRatio(button);
        });
    });

    return cropper;
}

/**
 * Maps Cropper.js data onto the payload of /api/files/{id}/edit/.
 */
function buildPayload(cropper) {
    const data = cropper.getData(true);
    return {
        rotate: data.rotate || 0,
        scale_x: data.scaleX < 0 ? -1 : 1,
        scale_y: data.scaleY < 0 ? -1 : 1,
        crop: {
            x: data.x,
            y: data.y,
            width: data.width,
            height: data.height,
        },
    };
}

/**
 * Nothing to do when the image is untouched and the crop covers all of it.
 * Saving anyway would re-encode the file and regenerate thumbnails for nothing.
 */
function isNoOp(payload, cropper) {
    const { naturalWidth, naturalHeight } = cropper.getImageData();
    const { rotate, scale_x: scaleX, scale_y: scaleY, crop } = payload;
    return (
        rotate % 360 === 0 &&
        scaleX === 1 &&
        scaleY === 1 &&
        crop.x === 0 &&
        crop.y === 0 &&
        crop.width === naturalWidth &&
        crop.height === naturalHeight
    );
}

async function saveEdit(fileId, payload) {
    const response = await fetch(`/api/files/${fileId}/edit/`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCookie('csrftoken'),
        },
        body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(
            data?.message || gettext('Nie udało się zapisać zdjęcia.'),
        );
    }
    return data;
}

/**
 * Opens the image editor for a file and saves the result in place.
 *
 * @param {string} fileId - UUID of the file.
 * @param {string} imageUrl - URL of the original image (not a thumbnail), so that
 *     the crop coordinates are already in the scale of the file being edited.
 * @param {{ onSaved?: (file: object) => void, onStart?: () => void,
 *           onError?: () => void,
 *           extraRatios?: Array<{ label: string, value: number }> }} options -
 *     extraRatios adds crop ratios specific to the context the editor was opened
 *     from, e.g. the exact shape of the slide slot the photo will land in.
 */
export async function openImageEditor(fileId, imageUrl, options = {}) {
    const { onSaved, onStart, onError, extraRatios = [] } = options;
    let cropper = null;
    let editor = null;
    let saving = false;

    const ratios = [
        ASPECT_RATIOS[0],
        ...extraRatios.map(({ label, value }) => ({
            label,
            value: String(value),
        })),
        ...ASPECT_RATIOS.slice(1),
    ];

    const { isConfirmed, value } = await prompt({
        title: gettext('Edycja zdjęcia'),
        html: buildEditorHtml(bustCache(imageUrl), ratios),
        confirmText: gettext('Zapisz'),
        width: 1040,
        customClass: { popup: 'modal__content image-editor-modal' },
        didOpen: (popup) => {
            editor = popup.querySelector('.image-editor');
            cropper = createCropperController(popup);
        },
        preConfirm: async () => {
            if (saving) return false;

            const payload = buildPayload(cropper);
            if (isNoOp(payload, cropper)) return { noOp: true };

            saving = true;
            editor.classList.add('is-saving');
            onStart?.();
            try {
                return { file: await saveEdit(fileId, payload) };
            } catch (error) {
                console.error('Image edit failed:', error);
                addAlert('error', error.message);
                onError?.();
                saving = false;
                editor.classList.remove('is-saving');
                return false; // keeps the dialog open so the edit is not lost
            }
        },
    });

    cropper?.destroy();

    if (!isConfirmed || !value?.file) return null;

    addAlert('success', gettext('Zdjęcie zostało zapisane.'));
    onSaved?.(value.file);
    return value.file;
}
